import React, { useMemo, useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Play, Layers, ChevronUp, ChevronDown, X } from 'lucide-react';
import NetflixRow from './NetflixRow';
import MediaCard from './MediaCard';
import { isNovidade, groupByCategoria, groupBySubcategoria } from './academiaUtils';
import { LEARNING_PATHS, matchPathCourses, MOCK_PREVIEW_COURSES } from './academiaMocks';

/**
 * Academia — layout Netflix (2026-06).
 * Topo do Início: boas-vindas + cards de categorias (a pedido do owner — o
 * hero gigante com thumbnail esticada saiu). Depois, fileiras horizontais.
 *
 * Reordenação (super admin):
 * - Seções: setas ↑↓ no cabeçalho da fileira → onReorderSections(lista de ids)
 * - Cards: setas ←→ no overlay → onReorderItems([{id, ordem}...]) — a ordem
 *   dos cards usa o campo `ordem` dos itens (global por item).
 */

// Seções cuja ordem de cards é controlável (as demais têm ordem própria:
// continue = progresso, recent = data)
const isRowReordenavel = (rowId) =>
  rowId === 'start' || rowId === 'recursos' ||
  rowId.startsWith('cat-') || rowId.startsWith('rec-');

const ordemSort = (a, b) =>
  (a.ordem ?? 0) - (b.ordem ?? 0) ||
  new Date(a.created_at || 0) - new Date(b.created_at || 0);

const AcademiaHome = ({
  section = 'inicio',            // inicio | treinamento | cursos | recursos
  cursos, treinamentos, recursos, progresso, loading,
  onOpenItem, onResumeCourse,
  isSuperAdmin, onEditItem, onDeleteItem,
  layoutSecoes = [], onReorderSections, onReorderItems,
  // Ordem das SUBCATEGORIAS por categoria ({"<categoria>": [nomes]}) +
  // callback das setas ↑↓ da subfileira (persiste no layout, super admin).
  layoutSubs = {}, onReorderSubs,
  // Filtro de categoria (cards do topo): vive no PAI (AcademiaPage) pra
  // SOBREVIVER ao abrir um vídeo e voltar — só some quando o usuário desmarca.
  categoryFilter = null, onCategoryFilter,
  t, isMobile,
}) => {
  const [expandedRow, setExpandedRow] = useState(null); // { title, items }
  const rowRefs = useRef({});

  // Trocar de seção fecha o "Ver todos" da seção anterior (auditoria)
  useEffect(() => { setExpandedRow(null); }, [section]);

  const cursosList = cursos || [];
  const treinosList = treinamentos || [];
  const recursosList = recursos || [];

  // Cursos em andamento (<100%) — auditoria: backend inclui concluídos
  const emAndamento = useMemo(
    () => (progresso?.cursos_em_andamento || []).filter(c => (c.percentual ?? 0) < 100),
    [progresso]
  );
  const progressMap = useMemo(
    () => Object.fromEntries((progresso?.cursos_em_andamento || []).map(c => [c.id, c.percentual])),
    [progresso]
  );

  // Preview com catálogo vazio: SÓ para o super admin (auditoria)
  const catalogoVazio = cursosList.length === 0 && treinosList.length === 0;
  const cursosEfetivos = (catalogoVazio && isSuperAdmin) ? MOCK_PREVIEW_COURSES : cursosList;

  const continueInfo = emAndamento[0] || null;

  // ----- fileiras (ordem natural; depois aplicamos layoutSecoes) -----
  const rows = useMemo(() => {
    const lista = [];
    const showCursos = section === 'inicio' || section === 'cursos';
    const showTreinos = section === 'inicio' || section === 'treinamento';
    const showRecursos = section === 'inicio' || section === 'recursos';

    // 1) Continuar assistindo
    if (showCursos && emAndamento.length > 0) {
      const items = emAndamento
        .map(p => cursosList.find(c => c.id === p.id))
        .filter(Boolean);
      if (items.length) {
        lista.push({ id: 'continue', title: t('academia.home.continue_title') || 'Continuar assistindo', items });
      }
    }

    // 2) Comece por aqui (trilha iniciante — mock separado p/ futura API)
    if (showCursos) {
      const startPath = LEARNING_PATHS[0];
      const startCourses = matchPathCourses(startPath, cursosEfetivos).slice().sort(ordemSort);
      if (startCourses.length) {
        lista.push({
          id: 'start',
          title: t('academia.home.row_start') || 'Comece por aqui',
          items: startCourses,
        });
      }
    }

    // 3) Uma fileira por categoria real (cursos + treinamentos da categoria)
    if (showCursos || showTreinos) {
      const pool = [
        ...(showCursos ? cursosEfetivos : []),
        ...(showTreinos ? treinosList : []),
      ];
      groupByCategoria(pool).forEach((items, categoria) => {
        if (items.length > 0) {
          const sorted = items.slice().sort(ordemSort);
          lista.push({ id: `cat-${categoria}`, title: categoria, items: sorted, sub: groupBySubcategoria(sorted) });
        }
      });
    }

    // (fileira "Novidades/Adicionados recentemente" REMOVIDA a pedido do owner
    //  em 2026-07 — o badge NOVO nos cards já sinaliza conteúdo recente)

    // 4) Recursos (biblioteca) — fileiras por categoria de material
    if (showRecursos && recursosList.length > 0) {
      if (section === 'recursos') {
        groupByCategoria(recursosList).forEach((items, categoria) => {
          const sorted = items.slice().sort(ordemSort);
          lista.push({ id: `rec-${categoria}`, title: categoria, items: sorted, sub: groupBySubcategoria(sorted) });
        });
      } else {
        lista.push({
          id: 'recursos',
          title: t('academia.home.row_resources') || 'Recursos & Templates',
          items: recursosList.slice().sort(ordemSort).slice(0, 12),
        });
      }
    }

    return lista;
  }, [section, emAndamento, cursosList, cursosEfetivos, treinosList, recursosList, t]);

  // ----- ordem das seções definida pelo admin (layoutSecoes) -----
  const orderedRows = useMemo(() => {
    if (!layoutSecoes.length) return rows;
    const pos = Object.fromEntries(layoutSecoes.map((id, i) => [id, i]));
    return rows.slice().sort((a, b) => {
      const pa = pos[a.id] ?? (1000 + rows.indexOf(a));
      const pb = pos[b.id] ?? (1000 + rows.indexOf(b));
      return pa - pb;
    });
  }, [rows, layoutSecoes]);

  // Move seção (admin): persiste a lista COMPLETA exibida, já trocada
  const moveSection = (rowId, dir) => {
    const ids = orderedRows.map(r => r.id);
    const idx = ids.indexOf(rowId);
    const alvo = idx + dir;
    if (idx < 0 || alvo < 0 || alvo >= ids.length) return;
    [ids[idx], ids[alvo]] = [ids[alvo], ids[idx]];
    onReorderSections?.(ids);
  };

  // Move card dentro da fileira/subfileira (admin): reatribui `ordem` sequencial
  // na LISTA recebida (pode ser a categoria inteira ou uma subcategoria).
  const moveItem = (list, item, dir) => {
    const idx = list.findIndex(i => i.id === item.id);
    const alvo = idx + dir;
    if (idx < 0 || alvo < 0 || alvo >= list.length) return;
    const nova = list.slice();
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    const updates = nova
      .map((it, i) => ({ id: it.id, ordem: i }))
      .filter((u, i) => (nova[i].ordem ?? 0) !== u.ordem && !nova[i].__mock);
    if (updates.length) onReorderItems?.(updates);
  };

  // (scrollToRow removido: os cards do topo agora FILTRAM em vez de rolar)

  // Filtro ATIVO (card do topo marcado): renderiza SÓ a fileira daquela
  // categoria; o resto some até desmarcar. Guard: se a categoria sumiu do
  // catálogo (item deletado), volta a mostrar tudo.
  const filtering = !!categoryFilter && orderedRows.some(r => r.id === categoryFilter);
  const visibleRows = filtering ? orderedRows.filter(r => r.id === categoryFilter) : orderedRows;

  // Setas de reordenar SEÇÃO (categoria) — só super admin, no header grande.
  // Com filtro ativo não faz sentido reordenar (só há 1 seção na tela).
  const sectionArrows = (rowId, rowIdx) => {
    if (!isSuperAdmin || filtering) return null;
    const up = rowIdx > 0;
    const down = rowIdx < orderedRows.length - 1;
    const btn = (enabled, onClick, Icon, label) => (
      <button
        onClick={enabled ? onClick : undefined} disabled={!enabled} title={label}
        style={{
          background: 'none', border: '1px solid var(--dp-border-subtle)', borderRadius: '6px',
          padding: '3px', cursor: enabled ? 'pointer' : 'default', display: 'flex',
          color: enabled ? 'var(--dp-text-secondary)' : 'var(--dp-border-medium)', opacity: enabled ? 1 : 0.4,
        }}
      >
        <Icon size={13} />
      </button>
    );
    return (
      <div style={{ display: 'flex', gap: '3px' }}>
        {btn(up, () => moveSection(rowId, -1), ChevronUp, t('academia.home.move_section_up') || 'Mover seção para cima')}
        {btn(down, () => moveSection(rowId, 1), ChevronDown, t('academia.home.move_section_down') || 'Mover seção para baixo')}
      </div>
    );
  };

  // Renderiza uma fileira do catálogo. Categoria COM subcategorias vira um
  // BLOCO: título grande + uma subfileira por subcategoria (cards numerados
  // pra a ordem ficar clara). Sem subcategoria = fileira única (como antes).
  const renderRow = (row, rowIdx) => {
    const reordenavel = isSuperAdmin && isRowReordenavel(row.id);
    const verTodosLabel = t('academia.home.ver_todos') || 'Ver todos';
    const prevLabel = t('academia.home.prev') || 'Anterior';
    const nextLabel = t('academia.home.next') || 'Próximo';

    const cards = (list, numbered) =>
      list.map((item, itemIdx) => (
        <MediaCard
          key={item.id}
          item={item}
          progress={progressMap[item.id]}
          isNew={row.newBadges ? isNovidade(item) : false}
          ordinal={numbered ? itemIdx + 1 : null}
          onOpen={row.id === 'continue' ? onResumeCourse : onOpenItem}
          isSuperAdmin={isSuperAdmin}
          onEdit={onEditItem}
          onDelete={onDeleteItem}
          onMoveLeft={reordenavel && itemIdx > 0 ? () => moveItem(list, item, -1) : null}
          onMoveRight={reordenavel && itemIdx < list.length - 1 ? () => moveItem(list, item, 1) : null}
          t={t}
          isMobile={isMobile}
        />
      ));

    // ── Categoria COM subcategorias: header grande + subfileiras ──
    if (row.sub?.hasSubs) {
      const { direct, subs } = row.sub;

      // Ordem das SUBCATEGORIAS: a salva pelo admin vence (layoutSubs[categoria]);
      // subcategorias novas (fora da lista salva) vão pro FIM, na ordem natural.
      const naturalNames = [...subs.keys()];
      const stored = (layoutSubs && layoutSubs[row.title]) || [];
      const posMap = Object.fromEntries(stored.map((n, i) => [n, i]));
      const orderedSubNames = naturalNames.slice().sort((a, b) =>
        (posMap[a] ?? 1000 + naturalNames.indexOf(a)) - (posMap[b] ?? 1000 + naturalNames.indexOf(b)));

      // Move a subfileira ↑↓ e persiste a lista COMPLETA já trocada
      const moveSub = (idx, dir) => {
        const alvo = idx + dir;
        if (alvo < 0 || alvo >= orderedSubNames.length) return;
        const nova = orderedSubNames.slice();
        [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
        onReorderSubs?.(row.title, nova);
      };

      const subRow = (title, list, subIdx = null) => (
        <NetflixRow
          key={title} variant="sub" title={title} count={list.length} isMobile={isMobile}
          verTodosLabel={verTodosLabel} prevLabel={prevLabel} nextLabel={nextLabel}
          onVerTodos={list.length > (isMobile ? 2 : 4)
            ? () => setExpandedRow({ title: `${row.title} · ${title}`, items: list })
            : null}
          onMoveUp={isSuperAdmin && subIdx != null && subIdx > 0 ? () => moveSub(subIdx, -1) : null}
          onMoveDown={isSuperAdmin && subIdx != null && subIdx < orderedSubNames.length - 1 ? () => moveSub(subIdx, 1) : null}
          moveUpLabel={t('academia.home.move_sub_up') || 'Mover subcategoria para cima'}
          moveDownLabel={t('academia.home.move_sub_down') || 'Mover subcategoria para baixo'}
        >
          {cards(list, true)}
        </NetflixRow>
      );
      return (
        <div key={row.id} ref={el => { rowRefs.current[row.id] = el; }} style={{ scrollMarginTop: '80px', marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <h2 style={{ margin: 0, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.4px', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
              {row.title}
              <span style={{ marginLeft: '8px', fontSize: '12.5px', fontWeight: 500, color: 'var(--dp-text-tertiary)' }}>{row.items.length}</span>
            </h2>
            {sectionArrows(row.id, rowIdx)}
          </div>
          {direct.length > 0 && subRow(t('academia.home.sub_general') || 'Vídeos gerais', direct)}
          {orderedSubNames.map((subName, i) => subRow(subName, subs.get(subName), i))}
        </div>
      );
    }

    // ── Fileira simples (comportamento atual) ──
    return (
      <div key={row.id} ref={el => { rowRefs.current[row.id] = el; }} style={{ scrollMarginTop: '80px' }}>
        <NetflixRow
          title={row.title} count={row.items.length} isMobile={isMobile}
          verTodosLabel={verTodosLabel} prevLabel={prevLabel} nextLabel={nextLabel}
          onVerTodos={row.items.length > (isMobile ? 2 : 4)
            ? () => setExpandedRow({ title: row.title, items: row.items })
            : null}
          onMoveUp={isSuperAdmin && !filtering && rowIdx > 0 ? () => moveSection(row.id, -1) : null}
          onMoveDown={isSuperAdmin && !filtering && rowIdx < orderedRows.length - 1 ? () => moveSection(row.id, 1) : null}
          moveUpLabel={t('academia.home.move_section_up') || 'Mover seção para cima'}
          moveDownLabel={t('academia.home.move_section_down') || 'Mover seção para baixo'}
        >
          {cards(row.items, isRowReordenavel(row.id))}
        </NetflixRow>
      </div>
    );
  };

  // ----- loading -----
  if (loading) {
    return (
      <div>
        {section === 'inicio' && (
          <div className="dp-skeleton" style={{ height: '130px', borderRadius: '16px', marginBottom: '24px' }} />
        )}
        {[0, 1].map(r => (
          <div key={r} style={{ marginBottom: '34px' }}>
            <div className="dp-skeleton" style={{ height: '18px', width: '220px', borderRadius: '6px', marginBottom: '14px' }} />
            <div style={{ display: 'flex', gap: '12px', overflow: 'hidden' }}>
              {Array.from({ length: isMobile ? 2 : 5 }).map((_, i) => (
                <div key={i} className="dp-skeleton" style={{
                  width: isMobile ? '72vw' : '340px', aspectRatio: '16 / 9',
                  borderRadius: '10px', flexShrink: 0,
                }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ----- "Ver todos": grid de thumbnails (padrão Netflix de listagem) -----
  if (expandedRow) {
    return (
      <div>
        <button
          onClick={() => setExpandedRow(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            color: 'var(--dp-blue-500)', fontSize: '13px', fontWeight: 600,
            fontFamily: 'var(--dp-font)',
          }}
        >
          <ArrowLeft size={15} /> {t('academia.labels.voltar') || 'Voltar'}
        </button>
        <h2 style={{
          margin: '0 0 16px 0', fontSize: '20px', fontWeight: 700, letterSpacing: '-0.4px',
          color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
        }}>
          {expandedRow.title}
          <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 500, color: 'var(--dp-text-tertiary)' }}>
            {expandedRow.items.length}
          </span>
        </h2>
        <div style={{
          display: 'grid', gap: '14px',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
        }}>
          {expandedRow.items.map((item, i) => (
            <MediaCard
              key={item.id}
              item={item}
              progress={progressMap[item.id]}
              isNew={isNovidade(item)}
              ordinal={i + 1}
              onOpen={onOpenItem}
              isSuperAdmin={isSuperAdmin}
              onEdit={onEditItem}
              onDelete={onDeleteItem}
              t={t}
              isMobile={false}
              width={0 /* grid controla a largura */}
            />
          ))}
        </div>
      </div>
    );
  }

  // ----- estado vazio: ciente da SEÇÃO -----
  const emptyBlock = (
    <div style={{
      textAlign: 'center', padding: '60px 20px',
      color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
    }}>
      <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-secondary)', margin: '0 0 6px 0' }}>
        {t('academia.home.empty_title') || 'Conteúdo chegando'}
      </p>
      <p style={{ fontSize: '13px', margin: 0 }}>
        {t('academia.home.empty_desc') || 'Os primeiros cursos e treinamentos serão publicados em breve.'}
      </p>
    </div>
  );
  if (orderedRows.length === 0 && section !== 'inicio') {
    return emptyBlock;
  }

  // Categorias para os cards do topo (conteúdo de aprendizado)
  const categoriaCards = section === 'inicio'
    ? orderedRows.filter(r => r.id.startsWith('cat-'))
    : [];

  return (
    <div>
      {/* Esconde a scrollbar dos trilhos (escopo da Academia, sem tocar CSS global) */}
      <style>{`.academia-hscroll{scrollbar-width:none;-ms-overflow-style:none}.academia-hscroll::-webkit-scrollbar{display:none}`}</style>

      {/* ===== BOAS-VINDAS (Início) ===== */}
      {section === 'inicio' && (
        <div
          className="dp-top-accent"
          style={{
            background: 'var(--dp-bg-card)', borderRadius: '16px',
            border: '1px solid var(--dp-border-subtle)',
            padding: isMobile ? '18px' : '22px 26px',
            display: 'flex', gap: '18px', alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            marginBottom: '20px',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
            <h2 style={{
              margin: '0 0 4px 0', fontSize: isMobile ? '18px' : '21px', fontWeight: 700,
              color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', letterSpacing: '-0.4px',
            }}>
              {t('academia.home.welcome_title') || 'Bem-vindo à Academia'}
            </h2>
            <p style={{
              margin: 0, fontSize: '13px', lineHeight: 1.5,
              color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
            }}>
              {t('academia.home.welcome_subtitle') || 'Trilhas, cursos e recursos para operar seus canais.'}
            </p>
          </div>

          {/* Continuar de onde parou (quando há progresso) */}
          {continueInfo && (
            <div style={{
              flexShrink: 0, width: isMobile ? '100%' : '320px',
              background: 'rgba(0,0,0,0.25)', borderRadius: '12px', padding: '12px 14px',
            }}>
              <p style={{
                margin: '0 0 6px 0', fontSize: '10px', fontWeight: 700,
                color: 'var(--dp-blue-500)', fontFamily: 'var(--dp-font)',
                textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                {t('academia.home.hero_continue_kicker') || 'Continue de onde parou'}
              </p>
              <p style={{
                margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600,
                color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {continueInfo.titulo}
              </p>
              <div style={{
                height: '4px', borderRadius: '999px',
                background: 'var(--dp-bg-input)', overflow: 'hidden', marginBottom: '10px',
              }}>
                <div style={{
                  height: '100%', borderRadius: '999px',
                  width: `${continueInfo.percentual}%`,
                  background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
                }} />
              </div>
              <button
                className="dp-btn-cta dp-btn-cta--sm"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => onResumeCourse({ id: continueInfo.id, tipo: 'curso' })}
              >
                <Play size={13} fill="#fff" />
                {t('academia.labels.continuar') || 'Continuar'} · {continueInfo.percentual}%
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== CARDS DE CATEGORIAS (Início) ===== */}
      {categoriaCards.length > 0 && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{
            display: 'grid', gap: '10px',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(190px, 1fr))',
          }}>
            {categoriaCards.map(row => {
              // FILTRO toggle: marca -> mostra SÓ esta seção; desmarca -> tudo.
              const active = categoryFilter === row.id;
              return (
                <div
                  key={row.id}
                  onClick={() => onCategoryFilter?.(active ? null : row.id)}
                  title={active ? (t('academia.home.filter_clear') || 'Mostrar todas as seções') : (t('academia.home.filter_only') || 'Mostrar só esta seção')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    background: active ? 'rgba(13,174,255,0.08)' : 'var(--dp-bg-card)',
                    borderRadius: '12px',
                    border: active ? '1px solid rgba(13,174,255,0.45)' : '1px solid var(--dp-border-subtle)',
                    padding: '12px 14px',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.22)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                  onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.transform = 'none'; } }}
                >
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '9px', flexShrink: 0,
                    background: active ? 'linear-gradient(135deg, #0DAEFF, #3B82F6)' : 'rgba(13,174,255,0.08)',
                    border: active ? 'none' : '1px solid rgba(13,174,255,0.22)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Layers size={15} style={{ color: active ? '#fff' : 'var(--dp-blue-500)' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0, fontSize: '12.5px', fontWeight: 600,
                      color: active ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.title}
                    </p>
                    <p style={{ margin: 0, fontSize: '10.5px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)' }}>
                      {row.items.length} {row.items.length === 1 ? (t('academia.home.item_one') || 'conteúdo') : (t('academia.home.badge_items') || 'conteúdos')}
                    </p>
                  </div>
                  {active
                    ? <X size={14} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
                    : <ArrowRight size={13} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Início sem nenhuma fileira: mensagem (welcome continua acima) */}
      {orderedRows.length === 0 && section === 'inicio' && emptyBlock}

      {/* ===== FILEIRAS (com filtro ativo: só a seção marcada) ===== */}
      {visibleRows.map((row, rowIdx) => renderRow(row, rowIdx))}
    </div>
  );
};

export default AcademiaHome;
